import {
  ApplicationConfig,
  ErrorHandler,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
  provideZoneChangeDetection,
  LOCALE_ID,
  inject,
} from '@angular/core';
import { ChunkLoadErrorHandler } from './core/errors/chunk-load-error.handler';
import { FirestoreDebugService } from './core/services/firestore-debug.service';
import { registerLocaleData } from '@angular/common';
import localeEsCo from '@angular/common/locales/es-CO';
import { provideRouter, withComponentInputBinding } from '@angular/router';
import { provideHttpClient } from '@angular/common/http';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { Chart, registerables } from 'chart.js';

import { routes } from './app.routes';

// Register Spanish (Colombia) locale for number formatting
registerLocaleData(localeEsCo, 'es-CO');

// Register all Chart.js components (scales, controllers, elements, plugins)
Chart.register(...registerables);
import { initializeApp, provideFirebaseApp } from '@angular/fire/app';
import { getAuth, provideAuth, connectAuthEmulator } from '@angular/fire/auth';
import { getFirestore, provideFirestore, connectFirestoreEmulator } from '@angular/fire/firestore';
import { getFunctions, provideFunctions, connectFunctionsEmulator } from '@angular/fire/functions';
import { getStorage, provideStorage, connectStorageEmulator } from '@angular/fire/storage';
import { SpanishPaginatorIntl } from './core/services/spanish-paginator-intl.service';
import { TenantDb, RootDb } from './core/data/tenant-collection';
import { FirestoreTenantDb, FirestoreRootDb } from './core/data/firestore-tenant-db';
import { environment } from '../environments/environment';

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    // Reload once when a lazy chunk fails to load (stale tab after a new deploy).
    { provide: ErrorHandler, useClass: ChunkLoadErrorHandler },
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes, withComponentInputBinding()),
    provideHttpClient(),
    provideAnimationsAsync(),
    { provide: LOCALE_ID, useValue: 'es-CO' },
    { provide: MatPaginatorIntl, useClass: SpanishPaginatorIntl },
    // TenantCollection seam — Firestore adapter in the app; tests provide InMemoryTenantDb.
    { provide: TenantDb, useExisting: FirestoreTenantDb },
    // Root seam — top-level/cross-tenant collections (subscriptions, invites, plan_catalog, …).
    { provide: RootDb, useExisting: FirestoreRootDb },
    provideFirebaseApp(() => initializeApp(environment.firebase)),
    provideAuth(() => {
      const auth = getAuth();
      if (environment.useEmulators && environment.emulators) {
        connectAuthEmulator(
          auth,
          `http://${environment.emulators.auth.host}:${environment.emulators.auth.port}`,
          { disableWarnings: true }
        );
      }
      return auth;
    }),
    provideFirestore(() => {
      const firestore = getFirestore();
      if (environment.useEmulators && environment.emulators) {
        connectFirestoreEmulator(
          firestore,
          environment.emulators.firestore.host,
          environment.emulators.firestore.port
        );
      }
      return firestore;
    }),
    provideFunctions(() => {
      const functions = getFunctions();
      if (environment.useEmulators && environment.emulators) {
        connectFunctionsEmulator(
          functions,
          environment.emulators.functions.host,
          environment.emulators.functions.port
        );
      }
      return functions;
    }),
    provideStorage(() => {
      const storage = getStorage();
      if (environment.useEmulators && environment.emulators) {
        connectStorageEmulator(
          storage,
          environment.emulators.storage.host,
          environment.emulators.storage.port
        );
      }
      return storage;
    }),
    provideAppInitializer(() => {
      const debug = inject(FirestoreDebugService);
      // Expose globally for easy console access: __fsDebug.enable(), .report(), .reset(), .disable()
      (window as unknown as { __fsDebug: FirestoreDebugService }).__fsDebug = debug;
      // Auto-enable if the user set the flag in localStorage
      if (
        typeof localStorage !== 'undefined' &&
        localStorage.getItem('firestoreDebug') === 'true'
      ) {
        debug.enable();
      }
    }),
  ],
};
